import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Reveal from './ui/Reveal';
import MediaImage from './ui/MediaImage';
import TiltCard from './ui/TiltCard';
import { homePreviewSlotKey, type HomePreview } from '../lib/homePreviewsApi';
import { useConfirmableLinkClick } from '../lib/useConfirmableLinkClick';
import { cn } from '../lib/cn';

function HomePreviewCard({ preview, delay }: { preview: HomePreview; delay: number }) {
  const onClick = useConfirmableLinkClick(preview.link, !!preview.confirm_leave);
  const image = (
    <TiltCard className="rounded-md">
      <MediaImage
        slotKey={homePreviewSlotKey(preview.id)}
        label={`photo — ${preview.label}`}
        height={220}
        className="group-hover:brightness-105 group-hover:saturate-110"
      />
    </TiltCard>
  );

  if (!preview.link) {
    return (
      <Reveal delay={delay}>
        <div className="block group">{image}</div>
      </Reveal>
    );
  }

  const isExternal = /^https?:\/\//.test(preview.link);
  return (
    <Reveal delay={delay}>
      {isExternal ? (
        <a
          href={preview.link}
          target="_blank"
          rel="noopener noreferrer"
          className="block no-underline transition-transform duration-250 hover:-translate-y-1 group"
          onClick={onClick}
        >
          {image}
        </a>
      ) : (
        <Link to={preview.link} className="block no-underline transition-transform duration-250 hover:-translate-y-1 group" onClick={onClick}>
          {image}
        </Link>
      )}
    </Reveal>
  );
}

export default function HomePreviewCarousel({ previews }: { previews: HomePreview[] }) {
  const scrollerRef = useRef<HTMLDivElement>(null);

  if (previews.length === 0) return null;

  const scrollByCard = (delta: number) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: delta * el.clientWidth * 0.8, behavior: 'smooth' });
  };

  return (
    <div className="relative max-w-page mx-auto sm:px-8">
      <div
        ref={scrollerRef}
        className="flex gap-5 overflow-x-auto overflow-y-hidden snap-x snap-mandatory scroll-smooth pt-2 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {previews.map((preview, i) => (
          <div
            key={preview.id}
            className={cn('snap-start shrink-0 w-[62%] sm:w-[42%] lg:w-[23%]', i === previews.length - 1 && 'mr-5 sm:mr-0')}
          >
            <HomePreviewCard preview={preview} delay={i * 0.06} />
          </div>
        ))}
      </div>

      {previews.length > 1 && (
        <>
          <button
            type="button"
            onClick={() => scrollByCard(-1)}
            aria-label="Scroll left"
            className="hidden sm:flex absolute left-1 top-[calc(50%-10px)] -translate-y-1/2 w-10 h-10 rounded-full bg-ink/70 text-paper items-center justify-center hover:bg-ink transition-colors border-none cursor-pointer"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            onClick={() => scrollByCard(1)}
            aria-label="Scroll right"
            className="hidden sm:flex absolute right-1 top-[calc(50%-10px)] -translate-y-1/2 w-10 h-10 rounded-full bg-ink/70 text-paper items-center justify-center hover:bg-ink transition-colors border-none cursor-pointer"
          >
            <ChevronRight size={18} />
          </button>
        </>
      )}
    </div>
  );
}
